import { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { CampaignRoutingFallback } from "@signalist/shared-types";

import { Dropdown } from "@/components/Dropdown/Dropdown";

import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import { RoutingCampaign } from "./utils/campaignRoutingTargets";

interface RoutingFallbackPickerProps {
	fallback: CampaignRoutingFallback;
	defaultCampaign: RoutingCampaign | undefined;
	onChange: (fallback: CampaignRoutingFallback) => void;
}

/** "No rule matched —": the list's default campaign, or no campaign at all. */
export const RoutingFallbackPicker: FunctionComponent<RoutingFallbackPickerProps> = ({
	fallback,
	defaultCampaign,
	onChange,
}) => {
	const { t } = useTranslation();

	const options = useMemo(
		() => [
			{
				id: "DEFAULT_CAMPAIGN" as const,
				title: defaultCampaign
					? t(`${ROUTING_I18N}.fallback.optionDefault`, { campaign: defaultCampaign.name })
					: t(`${ROUTING_I18N}.fallback.optionDefaultMissing`),
			},
			{ id: "NO_CAMPAIGN" as const, title: t(`${ROUTING_I18N}.fallback.optionNone`) },
		],
		[defaultCampaign, t]
	);

	return (
		<div
			className="flex flex-wrap items-center gap-2 rounded-lg border border-border-light bg-brand-white p-4"
			data-testid="routing-fallback"
		>
			<span className="text-sm text-text-meta">{t(`${ROUTING_I18N}.fallback.label`)}</span>
			<Dropdown
				floating
				portal
				data={options}
				defaultValue={options.find((option) => option.id === fallback)}
				handleSelect={(item) => onChange(item.id as CampaignRoutingFallback)}
				testId="routing-fallback-select"
			/>
		</div>
	);
};
